import DOMPurify from 'dompurify';

/**
 * Tags worth keeping in an email body. Email clients ignore most structural
 * HTML anyway, and anything not listed here is unwrapped rather than dropped,
 * so text content survives.
 */
const ALLOWED_TAGS = [
  'p', 'div', 'span', 'br', 'hr',
  'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'img',
];

// `class` and `id` are allowed through DOMPurify only so the Gmail/Docs wrapper
// cleanup below can still see `.gmail_quote`, `.gmail_signature`, and
// `docs-internal-guid` — they are stripped from every element afterwards, so
// nothing carrying a class or id survives into the returned HTML.
const ALLOWED_ATTR = ['href', 'title', 'src', 'alt', 'width', 'height', 'style', 'target', 'colspan', 'rowspan', 'class', 'id'];

/** Style properties that carry real formatting intent from a Gmail paste. */
const KEPT_STYLE_PROPS = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'text-decoration',
  'text-align',
  'font-size',
  'margin-left',
  'padding-left',
]);

/**
 * Gmail wraps pasted content in its own scaffolding and stamps a font-family
 * on nearly every node. Keeping that makes every campaign look like a forwarded
 * message, so it gets filtered out while real formatting is preserved.
 */
function cleanStyleAttribute(style: string): string {
  return style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const prop = decl.split(':')[0]?.trim().toLowerCase();
      return prop ? KEPT_STYLE_PROPS.has(prop) : false;
    })
    .join('; ');
}

function unwrap(element: Element): void {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) parent.insertBefore(element.firstChild, element);
  parent.removeChild(element);
}

/**
 * Sanitizes pasted HTML and strips Gmail/Docs authoring cruft.
 * Returns HTML safe to store and send.
 */
export function sanitizeEmailHtml(dirty: string): string {
  const clean = DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    // Unwrap disallowed tags instead of deleting their text.
    KEEP_CONTENT: true,
    // Block javascript:/data: URLs on href and src.
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|cid:|#)/i,
    RETURN_DOM_FRAGMENT: true,
  }) as unknown as DocumentFragment;

  const container = document.createElement('div');
  container.appendChild(clean);

  // Gmail's quote/signature wrappers and Google Docs' guid spans.
  container
    .querySelectorAll('.gmail_quote, .gmail_attr, .gmail_signature, [id^="docs-internal-guid"]')
    .forEach((el) => {
      if (el.classList.contains('gmail_quote') || el.classList.contains('gmail_attr')) {
        el.remove();
      } else {
        unwrap(el);
      }
    });

  container.querySelectorAll('*').forEach((el) => {
    el.removeAttribute('class');
    el.removeAttribute('id');
    el.removeAttribute('dir');

    const style = el.getAttribute('style');
    if (style) {
      const kept = cleanStyleAttribute(style);
      if (kept) el.setAttribute('style', kept);
      else el.removeAttribute('style');
    }

    if (el.tagName === 'A') {
      // Anchors in email should open away from any embedded viewer.
      el.setAttribute('target', '_blank');
      el.setAttribute('rel', 'noopener noreferrer');
    }

    // An image with no alt text is an accessibility failure in the inbox too.
    if (el.tagName === 'IMG' && !el.getAttribute('alt')) {
      el.setAttribute('alt', '');
    }

    // A span carrying no formatting is pure Gmail noise.
    if (el.tagName === 'SPAN' && !el.getAttribute('style')) {
      unwrap(el);
    }
  });

  return container.innerHTML.trim();
}
